import { useCallback, useEffect, useRef } from "react";
import { useNotification } from "@context/NotificationContext";
import { FORM_FIELDS } from "@features/vessel-call/create/constants";
import type { Passenger } from "@features/vessel-call/create/store/useVesselCallForm.store";
import { useVesselCallFormStore } from "@features/vessel-call/create/store/useVesselCallForm.store";
import type {
  ImportedPassengerEntry,
  ImportPassengerListResponse,
} from "@services/vesselCall/types";
import { importPassengerList } from "@services/vesselCallApi";
import { useMutation } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { useTranslation } from "react-i18next";

type PassengerImportError = {
  message?: string;
  response?: {
    data?: {
      detail?: string;
      message?: string;
      errors?: string[];
    };
  };
};

const getPassengerOrder = (passenger: ImportedPassengerEntry, fallbackIndex: number) => {
  const order =
    passenger.no ??
    passenger.passengerOrderToBeDisplayed ??
    passenger.passengerOrder ??
    passenger.order ??
    passenger.sequence;

  return typeof order === "number" && Number.isFinite(order) ? order : fallbackIndex + 1;
};

const mapImportedPassenger = (passenger: ImportedPassengerEntry, index: number): Passenger => ({
  id: `imported-passenger-${Date.now()}-${index + 1}`,
  order: getPassengerOrder(passenger, index),
  familyName: passenger.familyName ?? "",
  givenName: passenger.givenName ?? "",
  nationality: passenger.nationality ?? "",
  dateOfBirth: passenger.birthDate ?? "",
  placeOfBirth: passenger.birthPlace ?? "",
  gender: passenger.gender ?? "",
  typeOfIdentityDocument:
    passenger.identityOrTravelDocumentTypeReferenceCode ??
    passenger.identityOrTravelDocumentType ??
    "",
  identityDocumentNumber: passenger.identityOrTravelDocumentSerialNumber ?? "",
  issuingState:
    passenger.identityOrTravelDocumentIssuingStateReferenceCode ??
    passenger.identityOrTravelDocumentIssuingState ??
    "",
  expiryDate: passenger.identityOrTravelDocumentExpiryDate ?? "",
  visaNumberIfAppropriate: passenger.visaNumber ?? "",
  portOfEmbarkation: passenger.embarkationPortReferenceCode ?? passenger.embarkationPort ?? "",
  portOfDisembarkation:
    passenger.disembarkationPortReferenceCode ?? passenger.disembarkationPort ?? "",
  transitPassenger: Boolean(passenger.transitPassenger),
});

const extractErrorMessage = (error: PassengerImportError, fallback: string) => {
  const backendErrors = error.response?.data?.errors;
  if (Array.isArray(backendErrors) && backendErrors.length > 0) {
    return backendErrors.join(" ");
  }

  return error.response?.data?.detail ?? error.response?.data?.message ?? error.message ?? fallback;
};

const isAbortedRequest = (error: PassengerImportError) =>
  (error as { code?: string; name?: string }).code === "ERR_CANCELED" ||
  (error as { code?: string; name?: string }).name === "CanceledError" ||
  (error as { code?: string; name?: string }).name === "AbortError";

const normalizeImportedPassengers = (response: ImportPassengerListResponse) => {
  const passengers = Array.isArray(response) ? response : (response.passengers ?? []);

  return [...passengers]
    .sort((left, right) => getPassengerOrder(left, 0) - getPassengerOrder(right, 0))
    .map(mapImportedPassenger);
};

const IMPORT_PASSENGER_LIST_TRANSACTION_CONTEXT = {
  module: "vessel_call",
  screen: "FAL Passenger List",
  action: "import_passenger_list",
  entityType: "passenger_list",
};

export const useImportPassengerList = () => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const setPassengers = useVesselCallFormStore((state) => state.setPassengers);
  const setField = useVesselCallFormStore((state) => state.setField);
  const clearAddPassengerDraft = useVesselCallFormStore((state) => state.clearAddPassengerDraft);
  const importControllerRef = useRef<AbortController | null>(null);

  const abortImport = useCallback(() => {
    importControllerRef.current?.abort();
  }, []);

  useEffect(() => abortImport, [abortImport]);

  const mutation = useMutation({
    mutationFn: (file: File) => {
      importControllerRef.current?.abort();
      const controller = new AbortController();
      importControllerRef.current = controller;
      return runTransaction(IMPORT_PASSENGER_LIST_TRANSACTION_CONTEXT, (transactionId) =>
        importPassengerList(file, controller.signal, transactionId),
      );
    },
    onSuccess: (response: ImportPassengerListResponse) => {
      const importedPassengers = normalizeImportedPassengers(response);

      setPassengers(importedPassengers);
      setField(FORM_FIELDS.NUMBER_OF_PASSENGERS, String(importedPassengers.length));
      clearAddPassengerDraft();

      notify({
        title: t("vessel-call.create.fal-form-6.import-success-title"),
        description: t("vessel-call.create.fal-form-6.import-success-description"),
        type: "success",
      });
    },
    onError: (error: PassengerImportError) => {
      if (isAbortedRequest(error)) {
        return;
      }

      notify({
        title: t("vessel-call.create.fal-form-6.import-error-title"),
        description: extractErrorMessage(
          error,
          t("vessel-call.create.fal-form-6.import-error-description"),
        ),
        type: "error",
      });
    },
  });

  return { ...mutation, abortImport };
};
